// Start screen overlay. Legacy: dismissStartScreen / switchStartTab /
// renderStartSheets / renderStartPrinters / showRecentDesigns / startFromImage
// (snapshot L1863-1998, L2835-3124, L6498-6787). Rebuilt 1-to-1 on the live
// design: a full-screen overlay with search, tabs (Recent / Label Printers /
// Label Sheets / Custom Size), "start from scratch", an image dropzone, and a
// "don't show this screen again" checkbox backed by localStorage('olm-skip-start').
//
// No #startScreen element exists in index.html, so this module builds its own
// `fixed inset-0` overlay and appends it to #modalRoot (falling back to body).
import { state } from './state'
import { toMm, fmtUnit, customName, type Unit } from './units'
import { LABEL_SIZES, SHEET_LAYOUTS, MM_TO_PX } from './constants'
import { applySize, applySheet } from './panels/sizePicker'
import { addImageFromFile } from './widgets/image'
import { listDesigns, loadCloudDesign, type DesignSummary } from './cloud/designs'
import { el, toast } from './ui/dom'
import { icon } from './ui/icons'
import { aiImport, isAiImportConfigured } from './import-ai'
import { searchCatalogue, shapeOf, SHAPE_OPTIONS } from './search'
import { markBrand, showsAvery, averyDisclaimerEl } from './brandMark'
import { PRODUCT, logoHtml } from './product'

const SKIP_KEY = 'olm-skip-start'

let overlay: HTMLElement | null = null

export function closeStartScreen(): void {
  overlay?.remove()
  overlay = null
}

type StartTab = 'recent' | 'printers' | 'sheets' | 'custom'

/** Size-list predicate: when the product narrows the start screen to a category
 *  (e.g. invoice → 'document') filter to it; else today's behavior (all but custom). */
function inStartCategory(s: (typeof LABEL_SIZES)[number]): boolean {
  const cat = PRODUCT.start.sizeCategory
  return cat == null ? s.category !== 'custom' : s.category === cat
}

export function openStartScreen(): void {
  closeStartScreen()
  const root = document.getElementById('modalRoot') ?? document.body

  overlay = el('div', 'fixed inset-0 z-[300] flex items-center justify-center')
  overlay.style.background = 'rgba(15,23,42,0.95)'
  overlay.style.backdropFilter = 'blur(8px)'

  const panel = el('div', 'w-full max-w-3xl mx-4 md:mx-auto')
  panel.style.maxHeight = '90vh'
  panel.style.overflowY = 'auto'

  // ---- logo ----
  const logoWrap = el('div', 'flex items-center justify-center mb-6')
  const logo = el('span', 'text-2xl font-extrabold text-white tracking-tight')
  logo.innerHTML = logoHtml('text-blue-500')
  logoWrap.append(logo)

  // ---- search ----
  const searchWrap = el('div', 'max-w-lg mx-auto mb-5')
  const search = el('input', 'w-full h-10 rounded-lg border border-slate-600 bg-slate-800/80 text-white text-sm px-4 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20') as HTMLInputElement
  search.type = 'text'
  search.placeholder = PRODUCT.start.searchPlaceholder
  searchWrap.append(search)

  const searchResults = el('div', 'hidden mb-6')

  // ---- tabs ----
  let activeTab: StartTab = 'recent'
  const tabsRow = el('div', 'flex items-center justify-center gap-2 mb-5 flex-wrap')
  const tabDefs = PRODUCT.start.tabs as { id: StartTab; label: string }[]
  const tabBtns = new Map<StartTab, HTMLElement>()
  for (const def of tabDefs) {
    const b = el('button', 'start-tab', def.label)
    b.addEventListener('click', () => selectTab(def.id))
    tabBtns.set(def.id, b)
    tabsRow.append(b)
  }

  // ---- tab content hosts ----
  const recentTab = buildRecentTab()
  const printersTab = buildPrintersTab()
  const sheetsTab = buildSheetsTab()
  const customTab = buildCustomTab()
  const tabHosts: Record<StartTab, HTMLElement> = {
    recent: recentTab,
    printers: printersTab,
    sheets: sheetsTab,
    custom: customTab,
  }

  // ---- bottom row ----
  const bottom = el('div', 'flex items-center justify-between mt-6 px-2')
  const skipLabel = el('label', 'flex items-center gap-2 cursor-pointer group')
  const skip = el('input', 'w-3.5 h-3.5 rounded border-slate-600 bg-slate-800 accent-blue-500 cursor-pointer') as HTMLInputElement
  skip.type = 'checkbox'
  skip.checked = localStorage.getItem(SKIP_KEY) === '1'
  skip.addEventListener('change', () => {
    if (skip.checked) localStorage.setItem(SKIP_KEY, '1')
    else localStorage.removeItem(SKIP_KEY)
  })
  skipLabel.append(skip, el('span', 'text-[11px] text-slate-500 group-hover:text-slate-400 transition-colors', "Don't show this screen again"))
  const privacy = el('div', 'inline-flex items-center gap-1.5 text-[11px] text-slate-500')
  const lockIc = document.createElement('span')
  lockIc.innerHTML = icon('lock', 'w-3.5 h-3.5')
  privacy.append(lockIc, document.createTextNode('100% private'))
  bottom.append(skipLabel, privacy)

  // ---- search behaviour ----
  search.addEventListener('input', () => {
    const q = search.value.trim().toLowerCase()
    if (!q) { searchResults.classList.add('hidden'); searchResults.replaceChildren(); return }
    searchResults.classList.remove('hidden')
    searchResults.replaceChildren()
    const grid = el('div', 'space-y-1 max-h-[40vh] overflow-y-auto pr-1')
    // Ranked search over codes/notes/dims/perSheet/page size too (classic parity);
    // sheets only for products whose start screen offers them (label profile).
    const sizes = searchCatalogue(q, LABEL_SIZES.filter(inStartCategory))
    for (const sz of sizes) grid.append(sizeRow(sz.name, markBrand(`${sz.brand} · ${sz.widthMM}×${sz.heightMM} mm${codeHint(sz, q)}`), () => { applySize(sz); closeStartScreen() }))
    const sheets = PRODUCT.start.tabs.some((t) => t.id === 'sheets') ? searchCatalogue(q, SHEET_LAYOUTS) : []
    for (const sh of sheets) grid.append(sizeRow(sh.name, markBrand(`${sh.brand} · ${sh.columns}×${sh.rows} on ${sh.pageWidthMM}×${sh.pageHeightMM} mm${codeHint(sh, q)}`), () => { applySheet(sh, { query: q }); closeStartScreen() }))
    if (!grid.childNodes.length) grid.append(el('div', 'text-sm text-slate-500 text-center py-4', 'No matches.'))
    searchResults.append(grid)
    if (showsAvery(sizes) || showsAvery(sheets)) searchResults.append(averyDisclaimerEl())
  })

  function selectTab(id: StartTab): void {
    activeTab = id
    for (const [tid, b] of tabBtns) b.classList.toggle('active', tid === id)
    for (const [tid, host] of Object.entries(tabHosts)) host.classList.toggle('hidden', tid !== id)
  }

  panel.append(logoWrap, searchWrap, searchResults, tabsRow, recentTab, printersTab, sheetsTab, customTab, bottom)
  overlay.append(panel)
  root.append(overlay)
  selectTab('recent')
  search.focus()
}

// ---------- shared row helper ----------
function sizeRow(name: string, sub: string, onClick: () => void): HTMLElement {
  const card = el('div', 'size-card')
  const left = el('div')
  left.append(el('div', 'text-sm font-medium text-white', name))
  left.append(el('div', 'text-xs text-slate-400', sub))
  card.append(left)
  card.addEventListener('click', onClick)
  return card
}

// ---------- Recent tab ----------
function buildRecentTab(): HTMLElement {
  const host = el('div', 'start-tab-content')
  const scratch = el('button', 'w-full py-3 px-4 rounded-lg border border-slate-700 bg-slate-800/50 text-sm font-medium text-slate-300 hover:border-blue-500 hover:text-white transition-all mb-5 flex items-center justify-center gap-2')
  const ic = document.createElement('span')
  ic.innerHTML = icon('plus', 'w-4 h-4')
  scratch.append(ic, document.createTextNode('Start from scratch'))
  scratch.addEventListener('click', () => closeStartScreen())
  host.append(scratch)

  const recent = el('div', 'hidden')
  recent.append(el('div', 'text-xs text-slate-500 uppercase tracking-wider font-semibold mb-3 text-center', 'My Designs'))
  const grid = el('div', 'designs-grid')
  recent.append(grid)
  host.append(recent)

  // Populate recent designs (cloud) when signed in.
  if (state.currentUser) {
    void listDesigns().then((designs) => {
      if (!designs.length) return
      recent.classList.remove('hidden')
      for (const d of designs) grid.append(designCard(d))
    })
  }
  return host
}

function designCard(d: DesignSummary): HTMLElement {
  const card = el('div', 'design-card')
  const thumb = el('div', 'thumb')
  if (d.thumbnail) {
    const img = el('img') as HTMLImageElement
    img.src = d.thumbnail
    thumb.append(img)
  } else {
    const ic = document.createElement('span')
    ic.className = 'text-slate-600'
    ic.innerHTML = icon('image', 'w-6 h-6')
    thumb.append(ic)
  }
  card.append(thumb)
  card.append(el('div', 'name', d.name || 'Untitled'))
  card.append(el('div', 'date', formatDate(d.updated_at)))
  card.addEventListener('click', () => {
    void loadCloudDesign(d.id).then(() => closeStartScreen())
  })
  return card
}

function formatDate(iso: string): string {
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return ''
  return new Date(t).toLocaleDateString()
}

// ---------- Printers tab ----------
function buildPrintersTab(): HTMLElement {
  const host = el('div', 'start-tab-content hidden')
  let brand = 'all'
  const brandRow = el('div', 'flex gap-2 mb-4 flex-wrap')
  const list = el('div', 'space-y-2 max-h-[50vh] overflow-y-auto pr-1')

  const brands = ['all', ...distinct(LABEL_SIZES.filter((s) => inStartCategory(s)).map((s) => s.brand ?? 'Generic'))]
  const render = () => {
    for (const b of Array.from(brandRow.children) as HTMLElement[]) {
      const isActive = (b.dataset.brand ?? '') === brand
      if (isActive) { b.style.background = 'rgba(37,99,235,0.2)'; b.style.borderColor = '#2563eb'; b.style.color = '#e2e8f0' }
      else { b.style.background = ''; b.style.borderColor = ''; b.style.color = '' }
    }
    list.replaceChildren()
    const sizes = LABEL_SIZES.filter((s) => inStartCategory(s) && (brand === 'all' || (s.brand ?? '').toLowerCase() === brand.toLowerCase()))
    for (const sz of sizes) list.append(sizeRow(sz.name, markBrand(`${sz.brand} · ${sz.widthMM}×${sz.heightMM} mm`), () => { applySize(sz); closeStartScreen() }))
    if (!sizes.length) list.append(el('div', 'text-sm text-slate-500 text-center py-6', 'No sizes.'))
  }
  for (const br of brands) {
    const b = el('button', 'start-size-btn printer-brand-tab', br === 'all' ? 'All' : markBrand(br))
    b.dataset.brand = br
    b.addEventListener('click', () => { brand = br; render() })
    brandRow.append(b)
  }
  host.append(brandRow, list)
  render()
  return host
}

// ---------- Sheets tab ----------
function buildSheetsTab(): HTMLElement {
  const host = el('div', 'start-tab-content hidden')
  let brand = 'all'
  const brandRow = el('div', 'flex gap-2 mb-4 flex-wrap')
  host.append(el('h3', 'text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3', 'All sheet layouts'))
  const list = el('div', 'space-y-1 max-h-[40vh] overflow-y-auto pr-1')
  const disclaimer = averyDisclaimerEl()

  let shape = 'all'
  const shapeRow = el('div', 'flex gap-2 mb-3 flex-wrap')
  shapeRow.dataset.testid = 'start-sheet-shapes'
  const brands = ['all', ...distinct(SHEET_LAYOUTS.map((s) => s.brand ?? 'Avery'))]
  const render = () => {
    for (const b of Array.from(brandRow.children) as HTMLElement[]) {
      const isActive = (b.dataset.brand ?? '') === brand
      if (isActive) { b.style.background = 'rgba(37,99,235,0.2)'; b.style.borderColor = '#2563eb'; b.style.color = '#e2e8f0' }
      else { b.style.background = ''; b.style.borderColor = ''; b.style.color = '' }
    }
    for (const b of Array.from(shapeRow.children) as HTMLElement[]) {
      const isActive = (b.dataset.shape ?? '') === shape
      if (isActive) { b.style.background = 'rgba(37,99,235,0.2)'; b.style.borderColor = '#2563eb'; b.style.color = '#e2e8f0' }
      else { b.style.background = ''; b.style.borderColor = ''; b.style.color = '' }
    }
    list.replaceChildren()
    const sheets = SHEET_LAYOUTS.filter((s) =>
      (brand === 'all' || (s.brand ?? 'Avery').toLowerCase() === brand.toLowerCase() || (s.notes ?? '').toLowerCase().includes(brand.toLowerCase()))
      && (shape === 'all' || shapeOf(s) === shape))
    for (const sh of sheets) list.append(sizeRow(sh.name, markBrand(`${sh.brand} · ${sh.columns}×${sh.rows} on ${sh.pageWidthMM}×${sh.pageHeightMM} mm`), () => { applySheet(sh); closeStartScreen() }))
    if (!sheets.length) list.append(el('div', 'text-sm text-slate-500 text-center py-6', 'No sheets.'))
    disclaimer.classList.toggle('hidden', !showsAvery(sheets))
  }
  for (const br of brands) {
    const b = el('button', 'start-size-btn sheet-brand-tab', br === 'all' ? 'All' : markBrand(br))
    b.dataset.brand = br
    b.addEventListener('click', () => { brand = br; render() })
    brandRow.append(b)
  }
  for (const so of SHAPE_OPTIONS) {
    const b = el('button', 'start-size-btn', so.label)
    b.dataset.shape = so.id
    b.addEventListener('click', () => { shape = so.id; render() })
    shapeRow.append(b)
  }
  host.insertBefore(brandRow, host.firstChild)
  host.insertBefore(shapeRow, host.firstChild)
  host.append(list, disclaimer)
  render()
  return host
}

/** " · L7160" style hint when the query matched a product code (classic showed codes on cards). */
function codeHint(x: { codes?: string[] }, q: string): string {
  const n = q.trim().toLowerCase()
  if (!n) return ''
  const hit = (x.codes ?? []).find((c) => c.toLowerCase().includes(n))
  return hit ? ` · ${hit}` : ''
}

// ---------- Custom tab ----------
function buildCustomTab(): HTMLElement {
  const host = el('div', 'start-tab-content hidden')
  const inner = el('div', 'max-w-md mx-auto')
  inner.append(el('h3', 'text-lg font-bold text-white mb-4 text-center', 'Custom Size'))

  const row = el('div', 'flex gap-4 items-end justify-center mb-4')

  let wLbl: HTMLElement, hLbl: HTMLElement

  let unit: Unit = 'mm'
  const wWrap = el('div')
  wWrap.append((wLbl = el('label', 'text-xs text-slate-400 block mb-1', 'Width (mm)')))
  const wIn = el('input', 'prop-input') as HTMLInputElement
  wIn.type = 'number'; wIn.value = '89'; wIn.min = '10'; wIn.max = '500'; wIn.style.width = '100px'
  wWrap.append(wIn)
  const times = el('div', 'text-slate-500 text-lg pb-1', '×')
  const hWrap = el('div')
  hWrap.append((hLbl = el('label', 'text-xs text-slate-400 block mb-1', 'Height (mm)')))
  const hIn = el('input', 'prop-input') as HTMLInputElement
  hIn.type = 'number'; hIn.value = '36'; hIn.min = '10'; hIn.max = '500'; hIn.style.width = '100px'
  hWrap.append(hIn)
  const startBtn = el('button', 'tb-btn primary py-2', 'Start')
  startBtn.addEventListener('click', () => {
    const w = clampMm(toMm(parseFloat(wIn.value), unit))
    const h = clampMm(toMm(parseFloat(hIn.value), unit))
    applySize({ id: 'custom', name: customName(w, h, unit), brand: 'Custom', widthMM: w, heightMM: h, shape: 'rect', category: 'custom' })
    closeStartScreen()
  })
  // mm / inch toggle: inputs convert in place; sizes are stored in mm.
  const unitWrap = el('div')
  unitWrap.append(el('label', 'text-xs text-slate-400 block mb-1', 'Unit'))
  const unitSel = el('select', 'prop-input') as HTMLSelectElement
  for (const u of ['mm', 'in'] as Unit[]) { const o = el('option') as HTMLOptionElement; o.value = u; o.textContent = u === 'mm' ? 'mm' : 'inches'; unitSel.appendChild(o) }
  unitSel.addEventListener('change', () => {
    const next = unitSel.value as Unit
    const wMm = toMm(parseFloat(wIn.value) || 0, unit), hMm = toMm(parseFloat(hIn.value) || 0, unit)
    unit = next
    wIn.value = fmtUnit(wMm, unit); hIn.value = fmtUnit(hMm, unit)
    wIn.step = hIn.step = unit === 'in' ? '0.125' : '1'
    wIn.min = hIn.min = unit === 'in' ? '0.4' : '10'; wIn.max = hIn.max = unit === 'in' ? '20' : '500'
    wLbl.textContent = `Width (${unit})`; hLbl.textContent = `Height (${unit})`
  })
  unitWrap.append(unitSel)
  row.append(wWrap, times, hWrap, unitWrap, startBtn)
  inner.append(row)
  inner.append(el('div', 'text-xs text-slate-500 text-center mb-6', 'Enter your label dimensions in millimeters. Works with any printer.'))

  // divider
  const divider = el('div', 'flex items-center gap-3 mb-6')
  divider.append(el('div', 'flex-1 h-px bg-slate-700'), el('span', 'text-xs text-slate-500 font-medium', 'or design on an image'), el('div', 'flex-1 h-px bg-slate-700'))
  inner.append(divider)

  // image dropzone
  const drop = el('div', 'border-2 border-dashed border-slate-600 rounded-xl p-6 text-center cursor-pointer hover:border-blue-500 hover:bg-blue-500/5 transition-all')
  const ic = el('div', 'text-slate-500 flex justify-center mb-2')
  ic.innerHTML = icon('image', 'w-8 h-8')
  drop.append(ic)
  drop.append(el('div', 'text-sm text-slate-300 font-medium mb-1', 'Drop an image here or click to browse'))
  drop.append(el('div', 'text-xs text-slate-500', 'Canvas will match the image dimensions'))
  const fileInput = el('input', 'hidden') as HTMLInputElement
  fileInput.type = 'file'; fileInput.accept = 'image/*'
  drop.addEventListener('click', () => fileInput.click())
  drop.addEventListener('dragover', (e) => { e.preventDefault(); drop.classList.add('border-blue-500', 'bg-blue-500/5') })
  drop.addEventListener('dragleave', () => drop.classList.remove('border-blue-500', 'bg-blue-500/5'))
  drop.addEventListener('drop', (e) => {
    e.preventDefault()
    drop.classList.remove('border-blue-500', 'bg-blue-500/5')
    const f = (e as DragEvent).dataTransfer?.files?.[0]
    if (f) void startFromImage(f)
  })
  fileInput.addEventListener('change', () => {
    const f = fileInput.files?.[0]
    if (f) void startFromImage(f)
  })
  inner.append(drop, fileInput)

  // ---- AI: recreate an existing label as an editable design ----
  const aiDivider = el('div', 'flex items-center gap-3 my-6')
  aiDivider.append(
    el('div', 'flex-1 h-px bg-slate-700'),
    el('span', 'text-xs text-slate-500 font-medium', 'or recreate an existing label'),
    el('div', 'flex-1 h-px bg-slate-700'),
  )
  inner.append(aiDivider)

  const aiDrop = el('div', 'border-2 border-dashed border-blue-600/50 rounded-xl p-6 text-center cursor-pointer hover:border-blue-500 hover:bg-blue-500/5 transition-all')
  const aiIc = el('div', 'text-blue-400 flex justify-center mb-2')
  aiIc.innerHTML = icon('importData', 'w-8 h-8')
  aiDrop.append(aiIc)
  const titleRow = el('div', 'flex items-center justify-center gap-2 mb-1')
  titleRow.append(
    el('div', 'text-sm text-slate-200 font-medium', 'Recreate from a PDF or image'),
    el('span', 'text-[10px] uppercase tracking-wider font-bold text-blue-300 bg-blue-500/15 px-1.5 py-0.5 rounded', 'AI · Beta'),
  )
  aiDrop.append(titleRow)
  aiDrop.append(el('div', 'text-xs text-slate-500', 'We rebuild it as an editable design you can adapt'))
  const aiInput = el('input', 'hidden') as HTMLInputElement
  aiInput.type = 'file'
  aiInput.accept = 'image/*,application/pdf'
  aiDrop.addEventListener('click', () => aiInput.click())
  aiDrop.addEventListener('dragover', (e) => { e.preventDefault(); aiDrop.classList.add('border-blue-500', 'bg-blue-500/5') })
  aiDrop.addEventListener('dragleave', () => aiDrop.classList.remove('border-blue-500', 'bg-blue-500/5'))
  aiDrop.addEventListener('drop', (e) => {
    e.preventDefault()
    aiDrop.classList.remove('border-blue-500', 'bg-blue-500/5')
    const f = (e as DragEvent).dataTransfer?.files?.[0]
    if (f) void runAiImport(f)
  })
  aiInput.addEventListener('change', () => {
    const f = aiInput.files?.[0]
    if (f) void runAiImport(f)
  })
  inner.append(aiDrop, aiInput)
  if (!isAiImportConfigured()) {
    aiDrop.classList.add('opacity-50')
    aiDrop.append(el('div', 'text-[11px] text-amber-400/80 mt-2', 'Not enabled in this build'))
  }

  host.append(inner)
  return host
}

/** Send a reference to the AI import service and load the result. */
async function runAiImport(file: File): Promise<void> {
  if (!isAiImportConfigured()) {
    toast('AI import isn’t enabled in this build yet.', 4000)
    return
  }
  closeStartScreen()
  const loader = openImportLoading(file.name)
  try {
    const { mode, warnings } = await aiImport(file, 'chemical/safety or shipping label')
    loader.close()
    const extra = warnings?.length ? ' Some elements were approximated — check the layout.' : ''
    toast(`Label recreated (${mode}).${extra}`, 5000)
  } catch (e: any) {
    loader.fail(e?.message ?? 'unknown error')
  }
}

/** Full-screen overlay shown while the AI import runs (~30–60s, no progress events). */
function openImportLoading(filename: string): { close: () => void; fail: (msg: string) => void } {
  const root = document.getElementById('modalRoot') ?? document.body
  const overlay = el('div', 'fixed inset-0 z-[400] flex items-center justify-center')
  overlay.style.background = 'rgba(15,23,42,0.92)'
  overlay.style.backdropFilter = 'blur(8px)'

  // one-time keyframe for the spinner (avoids depending on a Tailwind utility)
  if (!document.getElementById('olm-spin-style')) {
    const st = document.createElement('style')
    st.id = 'olm-spin-style'
    st.textContent = '@keyframes olmspin{to{transform:rotate(360deg)}}'
    document.head.append(st)
  }

  const panel = el('div', 'text-center px-8 max-w-md')
  const spinner = el('div')
  spinner.style.cssText = 'width:48px;height:48px;margin:0 auto 20px;border:3px solid rgba(148,163,184,0.25);border-top-color:#3b82f6;border-radius:9999px;animation:olmspin 0.8s linear infinite'
  panel.append(spinner)
  panel.append(el('div', 'text-lg font-semibold text-white mb-1', 'Recreating your label with AI'))
  const status = el('div', 'text-sm text-slate-400 mb-3', 'Analysing the layout…')
  panel.append(status)
  panel.append(el('div', 'text-xs text-slate-500 truncate', filename))
  panel.append(el('div', 'text-[11px] text-slate-600 mt-4', 'This usually takes 30–60 seconds.'))
  overlay.append(panel)
  root.append(overlay)

  // staged status messages so the wait feels alive
  const stages = ['Analysing the layout…', 'Detecting text, fields and graphics…', 'Rebuilding editable elements…', 'Almost there…']
  let i = 0
  const timer = window.setInterval(() => {
    i = Math.min(i + 1, stages.length - 1)
    status.textContent = stages[i]
  }, 12000)

  const cleanup = () => { window.clearInterval(timer) }
  return {
    close() { cleanup(); overlay.remove() },
    fail(msg: string) {
      cleanup()
      spinner.style.animation = 'none'
      spinner.style.borderTopColor = '#ef4444'
      panel.replaceChildren(
        el('div', 'text-lg font-semibold text-white mb-2', 'Import failed'),
        el('div', 'text-sm text-slate-400 mb-5 break-words', msg),
      )
      const btn = el('button', 'tb-btn primary py-2 px-6', 'Close')
      btn.addEventListener('click', () => overlay.remove())
      panel.append(btn)
    },
  }
}

// ---------- helpers ----------
function clampMm(v: number): number {
  if (Number.isNaN(v)) return 50
  return Math.max(10, Math.min(500, v))
}

function distinct(arr: string[]): string[] {
  const out: string[] = []
  for (const x of arr) if (!out.includes(x)) out.push(x)
  return out
}

/** Resize the canvas to the image's dimensions (px @96dpi -> mm), then add it. */
async function startFromImage(file: File): Promise<void> {
  closeStartScreen()
  try {
    const dims = await imageDimsMm(file)
    applySize({ id: 'custom', name: `Image ${dims.w}×${dims.h} mm`, brand: 'Custom', widthMM: dims.w, heightMM: dims.h, shape: 'rect', category: 'custom' })
  } catch {
    /* fall through — add at current size */
  }
  await addImageFromFile(file)
}

function imageDimsMm(file: File): Promise<{ w: number; h: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve({ w: clampMm(Math.round(img.naturalWidth / MM_TO_PX)), h: clampMm(Math.round(img.naturalHeight / MM_TO_PX)) })
    }
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('image load failed')) }
    img.src = url
  })
}
